'use memo'

import { App } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { resolveDivisionFieldGates } from '@sportninja/common/features/competition/operations/utils/divisionFieldGates'
import {
  DIVISION_API_FIELD_MAP,
  DIVISION_API_TITLE_MAP,
} from '@sportninja/common/features/competition/operations/utils/divisionApiErrorFields'
import { processApiFieldErrors } from '@sportninja/common/utils/processApiFieldErrors'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import type { DivisionFormValues } from '@sportninja/common/features/competition/operations/utils/toDivisionPayload'
import type { OperationsScheduleNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import BaseModal from 'src/components/ts-components/BaseModal'
import {
  resolveEditType,
  resolveNewChildType,
} from '@sportninja/common/features/competition/operations/utils/resolveChildType'
import {
  useCreateDivision,
  useUpdateDivision,
} from '../hooks/useDivisionMutations'
import DivisionForm, { type DivisionFormState } from './DivisionForm'

const translate = t as (key: string, options?: unknown) => string

const EMPTY_FORM: DivisionFormState = {
  name: '',
  type: 'Division',
  lockRosterDate: null,
  sortOrder: '',
  isVisibleStandings: true,
  salesforceId: '',
  divisionGroup: '',
  divisionAge: '',
  divisionGender: '',
  dayOfWeek: '',
  // Legacy's `values.deposit || 0` / `values.team_cost || 0` defaults.
  deposit: '0',
  teamCost: '0',
  // Legacy defaults this to true when the node carries no value.
  isActive: true,
  // Legacy has NO default here — an unset node leaves the toggle off.
  onlineRegistrationOpen: false,
}

/** Money fields round to 2dp on submit, falling back to legacy's `0`. */
const toMoney = (value: string): string =>
  value && value !== '' ? parseFloat(value).toFixed(2) : '0'

/** Node money fields arrive as number or string; the inputs are string-based. */
const moneyToInput = (value: number | string | null): string =>
  value === null || value === undefined ? '0' : String(value)

interface DivisionModalProps {
  /**
   * CREATE: the node the child is created under.
   * EDIT: the node's own parent, unused for the payload but kept for symmetry.
   */
  parent: OperationsScheduleNode
  /** Present = edit that node; absent = create a child under `parent`. */
  node?: OperationsScheduleNode | null
  /**
   * Edit only: the node is the ROOT competition, whose name and type the
   * legacy panel locks (it is edited from the competition header instead).
   */
  isRoot?: boolean
  orgId: string
  rootScheduleId: string
  /** Canlan-only registration/Salesforce fields. */
  showSalesforceSettings?: boolean
  onClose: () => void
  onCreated: () => void
}

/**
 * Creates OR edits a division (conference / season too) — one dialog for both,
 * as the legacy Setup panel had. Pass `node` to edit it; omit it to create a
 * child under `parent`.
 *
 * The PAYLOAD and endpoints match the legacy Setup tab exactly (see
 * `toDivisionPayload`); only the UI is new — `BaseModal` plus the shared
 * ts-components inputs, so this dialog matches the rest of the app rather than
 * the legacy JSON `Form` config.
 *
 * The full `ScheduleModal` competition wizard is deliberately not used: its
 * Contact Info / Customization / DaySmart / Settings tabs do not apply here.
 */
const DivisionModal = ({
  parent,
  node,
  isRoot = false,
  orgId,
  rootScheduleId,
  showSalesforceSettings = false,
  onClose,
  onCreated,
}: DivisionModalProps) => {
  const { message } = App.useApp()
  const [values, setValues] = useState<DivisionFormState>(EMPTY_FORM)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const isEdit = Boolean(node)
  // What Type this dialog is for, and whether the user may change it. Editing
  // is always locked (re-typing would re-shape the tree); creating is locked
  // whenever the hierarchy or existing siblings already decide it.
  const resolvedType = node
    ? resolveEditType(node)
    : resolveNewChildType(parent)

  const { createDivision, isCreating } = useCreateDivision({
    orgId,
    rootScheduleId,
    showSalesforceSettings,
  })
  const { updateDivision, isUpdating } = useUpdateDivision({
    rootScheduleId,
    showSalesforceSettings,
  })
  const isBusy = isCreating || isUpdating

  // Reseed whenever the target changes: creating under another node must not
  // keep the previous entry, and editing must prefill from the node.
  useEffect(() => {
    setValues(
      node
        ? {
            name: node.name,
            type: resolvedType.type,
            lockRosterDate: node.lockRosterDate
              ? dayjs(node.lockRosterDate)
              : null,
            sortOrder: node.sortOrder === null ? '' : String(node.sortOrder),
            isVisibleStandings: node.isVisibleStandings ?? true,
            // Read back as `salesforce_id`, written as `import_2_id` — legacy
            // prefills the field from exactly this value.
            salesforceId: node.salesforceId ?? '',
            divisionGroup: node.divisionGroup ?? '',
            divisionAge: node.divisionAge ?? '',
            divisionGender: node.divisionGender ?? '',
            dayOfWeek: node.dayOfWeek ?? '',
            deposit: moneyToInput(node.deposit),
            teamCost: moneyToInput(node.teamCost),
            isActive: node.isActive ?? true,
            onlineRegistrationOpen: node.onlineRegistrationOpen ?? false,
          }
        : { ...EMPTY_FORM, type: resolvedType.type }
    )
    setFieldErrors({})
  }, [node, parent.scheduleId])

  // The same three gates the form renders on — shared so a hidden field is
  // never submitted, and a required one is only enforced when visible.
  const { showDivisionFields, showSalesforceId, showOnlineRegistration } =
    resolveDivisionFieldGates({
      type: values.type,
      showSalesforceSettings,
      isCanlanTenant: isCanlan,
    })

  const handleConfirm = async () => {
    const name = values.name.trim()
    if (!name) {
      setFieldErrors({ name: translate('common:required') })
      return
    }

    // The Canlan block's `division_age` / `division_gender` / `day_of_week` are
    // deliberately NOT enforced here. Legacy marks them `required` but its form
    // is create-blind, so divisions predating those fields were saved without
    // them — enforcing on edit would strand that data, unsaveable. The API is
    // the authority on what it will accept.

    const formValues: DivisionFormValues = {
      title: name,
      subtitle: values.type,
      sort_order: values.sortOrder === '' ? null : Number(values.sortOrder),
      is_visible_standings: values.isVisibleStandings,
      // The payload builder sends this date-only; null when unset.
      lock_roster_date: values.lockRosterDate
        ? values.lockRosterDate.toISOString()
        : null,
      // Only when the field was actually shown. `toDivisionPayload` sends this
      // key whenever it is truthy, independent of the Division gate below.
      ...(showSalesforceId && values.salesforceId
        ? { import_2_id: values.salesforceId }
        : {}),
      // The payload builder re-checks `subtitle === 'Division' &&
      // showSalesforceSettings` before sending these, so an unshown field
      // cannot leak into the body.
      ...(showDivisionFields
        ? {
            division_group: values.divisionGroup || null,
            division_age: values.divisionAge,
            division_gender: values.divisionGender,
            day_of_week: values.dayOfWeek,
            deposit: toMoney(values.deposit),
            team_cost: toMoney(values.teamCost),
            is_active: values.isActive,
            // No form inputs for these three — legacy's were removed under
            // SN-3238 / SN-2874 — but legacy still sends the node's existing
            // values on every edit. Omitting them would let the API clear
            // stored data on an unrelated save, so the round-trip is preserved.
            // Null on create (no node), which JSON.stringify drops, exactly as
            // legacy's own undefined does.
            season_type: node?.seasonType,
            season_currency: node?.seasonCurrency,
            tax_rate: node?.taxRate,
          }
        : {}),
      ...(showOnlineRegistration
        ? { online_registration_open: values.onlineRegistrationOpen }
        : {}),
    }

    try {
      if (node) {
        await updateDivision({ values: formValues, node })
      } else {
        await createDivision({
          values: formValues,
          parent,
          // The child inherits the owning ORGANISATION's visibility, matching
          // legacy. Only the tree root carries the organization block, so a
          // nested parent falls back to public — the API's own default.
          isPublic: parent.organizationIsPublic ?? true,
        })
      }
      // Named for the resolved type and the entered name, so the toast says
      // "Winter Division 1 edited successfully" rather than a bare "Saved".
      message.success(
        isEdit
          ? translate('Web:xEditedSuccessfully', {
              x: name,
              interpolation: { escapeValue: false },
            })
          : translate('Web:newXCreated', {
              x: translate(`common:${resolvedType.type.toLowerCase()}`),
            })
      )
      onCreated()
    } catch (error) {
      // Same contract as the player/family modals: map the API's
      // `invalid_fields` onto the inputs and let the inline messages be the
      // surface, falling back to a toast only when nothing maps.
      const { formErrors, hasFieldErrors } = processApiFieldErrors(
        (error as { invalid_fields?: Record<string, string | string[]> })
          ?.invalid_fields,
        {
          fieldMap: DIVISION_API_FIELD_MAP,
          titleMap: DIVISION_API_TITLE_MAP,
        }
      )

      if (hasFieldErrors && Object.keys(formErrors).length > 0) {
        setFieldErrors(formErrors)
        return
      }

      // `req` rejects with the parsed JSON body, not an Error — an
      // `instanceof Error` check would discard the API's own reason and show
      // the generic message instead. `getErrorMessage` reads every shape the
      // backend uses, matching `applyPlayerApiError`'s own fallback.
      message.error(getErrorMessage(error))
    }
  }

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      // A plain string lets BaseModal apply its own heading styling.
      // "Add Conference" / "Edit Division" etc — named for the resolved type
      // rather than always saying "Division".
      title={capitalizeWords(
        translate(isEdit ? 'common:editEntity' : 'common:addEntity', {
          entity: translate(`common:${resolvedType.type.toLowerCase()}`),
        })
      )}
      desktopWidth='672px'
      // BaseModal's default footer is the shared secondary/primary Button pair,
      // so no custom actions node is needed here.
      onOk={handleConfirm}
      okText={translate('common:confirm')}
      cancelText={translate('common:cancel')}
      isLoading={isBusy}
    >
      {/* The node's UIDs live in the panel header (`PanelHeader`), beside the
          Edit button, exactly as a team node's do — not in this dialog. */}
      <DivisionForm
        values={values}
        onChange={(next) => {
          // Clear errors as soon as the user edits anything — validation here is
          // submit-time, so a stale message would otherwise persist.
          setFieldErrors((prev) => (Object.keys(prev).length === 0 ? prev : {}))
          setValues(next)
        }}
        fieldErrors={fieldErrors}
        parentType={isEdit ? null : parent.type}
        // The root competition's name/type are owned by the competition header,
        // and Type locks once siblings exist at this depth — both mirroring the
        // legacy panel's `disableEditing` / `hasCousinNodes` rules.
        lockName={isRoot}
        lockType={isRoot || resolvedType.locked}
        disabled={isBusy}
        showSalesforceSettings={showSalesforceSettings}
        isCanlanTenant={isCanlan}
      />
    </BaseModal>
  )
}

export default DivisionModal
